import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ENV, IEnvironment, ApiResponse, ApiErrorResponse, IExternalProductResponse, ApiBanK } from '@shabic/models';
import { MediaService } from './media.service';

export interface IProductBankUploadResponse {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  message: string;
  processedFiles: number;
  totalFiles: number;
  errors?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ProductBankUploadService extends ApiBanK {
  override readonly url = '/bank/products';

  constructor(
    private http: HttpClient,
    @Inject(ENV) env: IEnvironment,
    private mediaService: MediaService
  ) {
    super(env);
  }

  uploadProductBankFiles(
    zipFile: File,
    excelFile: File,
    description?: string
  ): Observable<ApiResponse<IProductBankUploadResponse | ApiErrorResponse>> {
    const formData = new FormData();
    formData.append('file', excelFile);
    formData.append('type', 'EXCEL');
    formData.append('images', zipFile);
    if (description) {
      formData.append('description', description);
    }
    return this.http
      .post<IProductBankUploadResponse>(this.getEndpoint('/parse'), formData)
      .pipe(
        map(response => new ApiResponse(response)),
        catchError(error => of(new ApiResponse(new ApiErrorResponse(error))))
      );
  }

  getUploadStatus(uploadId: string): Observable<ApiResponse<IProductBankUploadResponse | ApiErrorResponse>> {
    return this.http
      .get<IProductBankUploadResponse>(this.getEndpoint(`/parse/${uploadId}/status`))
      .pipe(
        map(response => new ApiResponse(response)),
        catchError(error => of(new ApiResponse(new ApiErrorResponse(error))))
      );
  }

  getUploadHistory(): Observable<ApiResponse<IProductBankUploadResponse[] | ApiErrorResponse>> {
    return this.http
      .get<IProductBankUploadResponse[]>(this.getEndpoint('/parse/history'))
      .pipe(
        map(response => new ApiResponse(response)),
        catchError(error => of(new ApiResponse(new ApiErrorResponse(error))))
      );
  }

  deleteUpload(uploadId: string): Observable<ApiResponse<unknown | ApiErrorResponse>> {
    return this.http
      .delete(this.getEndpoint(`/parse/${uploadId}`))
      .pipe(
        map(response => new ApiResponse(response)),
        catchError(error => of(new ApiResponse(new ApiErrorResponse(error))))
      );
  }

  getBankProducts(params: Record<string, any> = {}): Observable<ApiResponse<any | ApiErrorResponse>> {
    const query = Object.entries(params)
      .filter(([_, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    const url = this.apiModelUrl + (query ? '?' + query : '');
    
    console.log('Product Bank API URL:', url);
    console.log('Product Bank API Params:', params);
    
    return this.http.get<any>(url).pipe(
      map(response => {
        console.log('Product Bank API Raw Response:', response);
        return new ApiResponse(response);
      }),
      catchError(error => {
        console.error('Product Bank API Error:', error);
        return of(new ApiResponse(new ApiErrorResponse(error)));
      })
    );
  }

  updateProductApprovingStatus(productId: string, approvingStatus: string): Observable<any> {
    return this.http.put(
      this.getEndpoint(`/${productId}/approvingStatus`),
      { approvingStatus }
    );
  }

  updateProduct(productId: string, updatedProduct: Partial<IExternalProductResponse>): Observable<ApiResponse<any | ApiErrorResponse>> {
    // Include the productId in the request body as expected by the backend
    const requestBody = {
      ...updatedProduct,
      productId: productId
    };
    
    return this.http.put(
      this.getEndpoint(''),
      requestBody
    ).pipe(
      map(response => new ApiResponse(response)),
      catchError(error => {
        console.error('Product Bank Update Error:', error);
        return of(new ApiResponse(new ApiErrorResponse(error)));
      })
    );
  }

  uploadMedia(files: File[]): Observable<ApiResponse<any>> {
    if (files.length === 0) {
      return of(new ApiResponse([]));
    }
    
    return this.mediaService.upload(
      this.getApiEndpoint('/media'),
      files,
      'PRODUCT'
    );
  }
} 